import { useFieldGroupData } from "./useFieldGroupData";
import { useFieldGroupReset } from "./useFieldGroupReset";
import { useFieldGroupSave } from "./useFieldGroupSave";
import { useFieldGroupSync } from "./useFieldGroupSync";

export interface UseFieldGroupEditorParams {
  isVisible: boolean;
  fieldGroup: string;
  isClosing?: boolean;
}

export interface UseFieldGroupEditorReturn {
  isSaving: boolean;
  isLoading: boolean;
  validationError: string | null;
  duplicateColumnName: string | null;
  handleSave: () => Promise<boolean>;
  clearValidationError: () => void;
  resetAllState: () => void;
  refetchFieldGroupData: () => void;
}

/**
 * Coordinates data fetching, state synchronization, save operations, and reset logic.
 */
export function useFieldGroupEditor(
  params: UseFieldGroupEditorParams
): UseFieldGroupEditorReturn {
  const { isVisible, fieldGroup, isClosing } = params;

  const fieldGroupData = useFieldGroupData({ fieldGroup, isVisible });

  const fieldGroupSave = useFieldGroupSave({ fieldGroup, isVisible });

  useFieldGroupSync({ fieldGroup, isVisible });

  const fieldGroupReset = useFieldGroupReset();

  return {
    // Don't show loading spinner when closing (after save) to prevent flash
    isLoading: fieldGroupData.isFetching && !isClosing,
    isSaving: fieldGroupSave.isSaving,
    validationError: fieldGroupSave.validationError,
    duplicateColumnName: fieldGroupSave.duplicateColumnName,
    handleSave: fieldGroupSave.handleSave,
    clearValidationError: fieldGroupSave.clearValidationError,
    resetAllState: fieldGroupReset.resetAllState,
    refetchFieldGroupData: fieldGroupData.refetch
  };
}
